/**
 * The Agents console's ticket filter rows (TRUS-687): one row of multi-select chips per
 * axis (OR within a row, AND across rows), a searchable Org dropdown, and the strip of
 * active filters with "Clear all".
 *
 * The page decides WHICH axes exist and their options/counts (from the server rollups);
 * an axis with no options and nothing selected is not shown, so an older backend that
 * sends none of the new rollups simply shows the rows it always did.
 */
import { useMemo, useRef, useState, useEffect, type ReactNode } from 'react';
import { Building2, ChevronDown, X } from 'lucide-react';
import { tvTokens } from '../../../shared/components/triveny';
import {
  JIRA_CATEGORY_LABEL,
  ORIGIN_LABEL,
  ORIGIN_ORDER,
  WAITING_ON_LABEL,
  type RcaAxis,
  type RcaFilterState,
  type RcaRollups,
  type RollupEntry,
  type WaitingOn,
} from './rcaFilterModel';
import { filterChip, filterLabel, linkButton, popover, smallButton } from './rcaStyles';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  /** Colour of the count (urgency tones on priority/due/journey). */
  tone?: string;
}

export interface FilterAxisDef {
  axis: RcaAxis;
  label: string;
  icon?: ReactNode;
  options: FilterOption[];
  /** A leading "All" chip that clears the axis (Type, Resolution). */
  allChip?: { label: string; count: number };
  variant?: 'chips' | 'dropdown';
}

/** Row titles for the active-filter strip (axes the page may not be showing right now). */
export const AXIS_TITLE: Record<RcaAxis, string> = {
  status: 'Resolution',
  verdict: 'Type',
  journey: 'Needs action',
  label: 'Label',
  priority: 'Priority',
  due: 'Due',
  org: 'Org',
  origin: 'Origin',
  reporter: 'Reporter',
  assignee: 'Assignee',
  issue_type: 'Issue type',
  jira_category: 'Jira status',
  has_pr: 'Has PR',
  reviewed_by: 'Reviewed by',
  waiting_on: 'Waiting on',
  waiting_on_exclude: 'Hide waiting on',
  reporter_exclude: 'Hide reporter',
  org_exclude: 'Hide org',
};

function fallbackValueLabel(axis: RcaAxis, value: string): string {
  if (axis === 'origin') return ORIGIN_LABEL[value] ?? value;
  if (axis === 'jira_category') return JIRA_CATEGORY_LABEL[value] ?? value;
  if (axis === 'has_pr') return value === 'true' ? 'Yes' : 'No';
  if (axis === 'waiting_on' || axis === 'waiting_on_exclude') return WAITING_ON_LABEL[value as WaitingOn] ?? value;
  if (value === 'me') return 'Me';
  if (value === 'none') return axis === 'assignee' ? 'Unassigned' : axis === 'org' || axis === 'org_exclude' ? 'No org' : 'None';
  if (axis === 'org' || axis === 'org_exclude') return `cbo ${value}`;
  if (axis === 'reporter' || axis === 'assignee' || axis === 'reviewed_by' || axis === 'reporter_exclude') return `user ${value}`;
  return value;
}

const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function peopleOptions(entries: RollupEntry[] | null, extra: FilterOption[], noneLabel: string): FilterOption[] {
  const out: FilterOption[] = [...extra];
  for (const e of entries ?? []) {
    if (e.count <= 0) continue;
    if (e.id === 'none') {
      const existing = out.find((o) => o.value === 'none');
      if (existing) existing.count = e.count;
      else out.push({ value: 'none', label: noneLabel, count: e.count });
      continue;
    }
    out.push({ value: e.id, label: e.name || `user ${e.id}`, count: e.count });
  }
  return out;
}

/**
 * The provenance & people axes (TRUS-687), built from the server rollups. Each is
 * omitted when its rollup is missing (older backend), so nothing renders with no data.
 */
export function buildProvenanceAxes(r: RcaRollups, paged: boolean): FilterAxisDef[] {
  const defs: FilterAxisDef[] = [];
  if (r.origin) {
    const options = ORIGIN_ORDER.filter((k) => (r.origin?.[k] ?? 0) > 0).map((k) => ({
      value: k,
      label: ORIGIN_LABEL[k],
      count: r.origin?.[k] ?? 0,
    }));
    // Origins the server knows and we don't label yet still get a chip.
    for (const [k, n] of Object.entries(r.origin)) {
      if (!ORIGIN_LABEL[k] && n > 0) options.push({ value: k, label: k, count: n });
    }
    defs.push({ axis: 'origin', label: 'Origin', options });
  }
  if (r.org) {
    const options = r.org
      .filter((e) => e.count > 0)
      .map((e) => ({ value: e.id, label: e.id === 'none' ? 'No org' : e.name || `cbo ${e.id}`, count: e.count }))
      .sort((a, b) => (a.value === 'none' ? -1 : b.value === 'none' ? 1 : a.label.localeCompare(b.label)));
    defs.push({ axis: 'org', label: 'Org', options, variant: 'dropdown', icon: <Building2 size={11} /> });
  }
  if (r.reporter) {
    defs.push({ axis: 'reporter', label: 'Reporter', options: peopleOptions(r.reporter, [{ value: 'me', label: 'Me' }], 'Unknown') });
  }
  if (r.assignee) {
    defs.push({
      axis: 'assignee',
      label: 'Assignee',
      options: peopleOptions(r.assignee, [{ value: 'me', label: 'Me' }, { value: 'none', label: 'Unassigned' }], 'Unassigned'),
    });
  }
  if (r.issue_type) {
    const options = Object.entries(r.issue_type)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([k, n]) => ({ value: k, label: k === 'none' ? 'None' : titleCase(k), count: n }));
    defs.push({ axis: 'issue_type', label: 'Issue type', options });
  }
  if (r.jira_category) {
    const options = Object.keys(JIRA_CATEGORY_LABEL)
      .filter((k) => (r.jira_category?.[k] ?? 0) > 0)
      .map((k) => ({ value: k, label: JIRA_CATEGORY_LABEL[k], count: r.jira_category?.[k] ?? 0 }));
    defs.push({ axis: 'jira_category', label: 'Jira status', options });
  }
  if (r.has_pr) {
    const options = (['true', 'false'] as const)
      .filter((k) => (r.has_pr?.[k] ?? 0) > 0)
      .map((k) => ({ value: k, label: k === 'true' ? 'Yes' : 'No', count: r.has_pr?.[k] ?? 0 }));
    defs.push({ axis: 'has_pr', label: 'Has PR', options });
  }
  if (paged) defs.push({ axis: 'reviewed_by', label: 'Reviewed', options: [{ value: 'me', label: 'Reviewed by me' }] });
  return defs;
}

/** An axis's options plus any selected value the rollup doesn't list (so it can be unselected). */
function withSelected(def: FilterAxisDef, selected: string[]): FilterOption[] {
  const out = [...def.options];
  for (const v of selected) {
    if (!out.some((o) => o.value === v)) out.push({ value: v, label: fallbackValueLabel(def.axis, v), count: 0 });
  }
  return out;
}

function Count({ n, active, tone }: { n?: number; active: boolean; tone?: string }) {
  if (n == null) return null;
  return <span style={{ color: active ? tvTokens.accentInk : tone ?? tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>{n}</span>;
}

function OrgDropdown({ def, selected, onToggle, onClear }: { def: FilterAxisDef; selected: string[]; onToggle: (v: string) => void; onClear: () => void }) {
  const [open, setOpen] = useState(false);
  // What the reviewer types to find an org: component state, never the URL.
  const [find, setFind] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const options = withSelected(def, selected);
  const needle = find.trim().toLowerCase();
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const summary =
    selected.length === 0
      ? 'Any org'
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? fallbackValueLabel('org', selected[0])
        : `${selected.length} orgs`;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid="rca-org-dropdown"
        style={{ ...filterChip(selected.length > 0), display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        {summary} <ChevronDown size={11} />
      </button>
      {open && (
        <div style={popover} role="listbox" aria-multiselectable="true" aria-label="Filter by org">
          <input
            autoFocus
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="Find an org"
            aria-label="Find an org"
            style={{ width: '100%', height: 28, padding: '0 8px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ marginTop: 6, maxHeight: 240, overflowY: 'auto' }}>
            {shown.length === 0 && <div style={{ padding: '6px 4px', fontSize: 12, color: tvTokens.ink400 }}>No org matches.</div>}
            {shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <label key={o.value} role="option" aria-selected={on} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px', fontSize: 12, color: tvTokens.ink700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={on} onChange={() => onToggle(o.value)} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                  <Count n={o.count} active={false} />
                </label>
              );
            })}
          </div>
          {selected.length > 0 && (
            <button type="button" onClick={onClear} style={{ ...linkButton, marginTop: 6 }}>
              Any org
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function RcaFilterBar({
  axes,
  state,
  onToggle,
  onClearAxis,
}: {
  axes: FilterAxisDef[];
  state: RcaFilterState;
  onToggle: (axis: RcaAxis, value: string) => void;
  onClearAxis: (axis: RcaAxis) => void;
}) {
  return (
    <>
      {axes.map((def) => {
        const selected = state.axes[def.axis] ?? [];
        const options = withSelected(def, selected);
        if (options.length === 0 && !def.allChip) return null;
        return (
          <div key={def.axis} data-testid={`rca-axis-${def.axis}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            <span style={filterLabel}>
              {def.icon}
              {def.label}
            </span>
            {def.variant === 'dropdown' ? (
              <OrgDropdown def={def} selected={selected} onToggle={(v) => onToggle(def.axis, v)} onClear={() => onClearAxis(def.axis)} />
            ) : (
              <>
                {def.allChip && (
                  <button type="button" onClick={() => onClearAxis(def.axis)} aria-pressed={selected.length === 0} style={filterChip(selected.length === 0)}>
                    {def.allChip.label} <Count n={def.allChip.count} active={selected.length === 0} />
                  </button>
                )}
                {options.map((o) => {
                  const active = selected.includes(o.value);
                  return (
                    <button key={o.value} type="button" onClick={() => onToggle(def.axis, o.value)} aria-pressed={active} style={filterChip(active)}>
                      {o.label} <Count n={o.count} active={active} tone={o.tone} />
                    </button>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

/** The active filters as removable chips, plus "Clear all". Renders nothing when none are set. */
export function RcaActiveFilters({
  axes,
  state,
  q,
  onRemove,
  onClearQ,
  onClearAll,
}: {
  axes: FilterAxisDef[];
  state: RcaFilterState;
  q: string;
  onRemove: (axis: RcaAxis, value: string) => void;
  onClearQ: () => void;
  onClearAll: () => void;
}) {
  const byAxis = useMemo(() => new Map(axes.map((d) => [d.axis, d])), [axes]);
  const chips: { axis: RcaAxis; value: string; text: string }[] = [];
  for (const axis of Object.keys(state.axes) as RcaAxis[]) {
    for (const value of state.axes[axis] ?? []) {
      const def = byAxis.get(axis);
      const label = def?.options.find((o) => o.value === value)?.label ?? fallbackValueLabel(axis, value);
      chips.push({ axis, value, text: `${def?.label ?? AXIS_TITLE[axis]}: ${label}` });
    }
  }
  const query = q.trim();
  if (chips.length === 0 && !query) return null;
  const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 4px 2px 9px',
    borderRadius: 20,
    border: `1px solid ${tvTokens.accent}`,
    background: tvTokens.accentSoft,
    color: tvTokens.accentInk,
    fontSize: 11,
    fontWeight: 600,
  };
  const x: React.CSSProperties = { display: 'inline-flex', padding: 1, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', borderRadius: 8 };
  return (
    <div data-testid="rca-active-filters" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      <span style={filterLabel}>Active</span>
      {chips.map((c) => (
        <span key={`${c.axis}:${c.value}`} style={chipStyle}>
          {c.text}
          <button type="button" aria-label={`Remove ${c.text}`} onClick={() => onRemove(c.axis, c.value)} style={x}>
            <X size={11} />
          </button>
        </span>
      ))}
      {query && (
        <span style={chipStyle}>
          Search: {query.length > 40 ? `${query.slice(0, 40)}…` : query}
          <button type="button" aria-label="Clear the search" onClick={onClearQ} style={x}>
            <X size={11} />
          </button>
        </span>
      )}
      <button type="button" onClick={onClearAll} style={{ ...smallButton, padding: '2px 8px' }}>
        Clear all
      </button>
    </div>
  );
}
